import * as React from "react";

import { cn } from "./cn";
import {
  addDays,
  compareDates,
  formatDateLong,
  formatMonth,
  type DateRange,
  type IsoDate,
  type IsoMonth,
  monthGrid,
  monthOf,
  shiftMonth,
  startOfWeek,
  WEEKDAY_SHORT,
} from "./date-range";
import { focusRing } from "./recipes";

type CalendarProps = {
  /** The first month on screen, `YYYY-MM`. */
  month: IsoMonth;
  onMonthChange: (month: IsoMonth) => void;
  /** Two side by side on a desktop popover, one on a phone. */
  numberOfMonths?: 1 | 2;
  /** The chosen range; a single date is a range whose ends are equal. */
  selected: DateRange;
  /**
   * The first click of a range still waiting for its second. While set, the
   * calendar previews anchor → the hovered or focused day.
   */
  anchor?: IsoDate | null;
  onSelect: (date: IsoDate) => void;
  today: IsoDate;
  /** Days outside [min, max] cannot be chosen. */
  min?: IsoDate;
  max?: IsoDate;
  className?: string;
};

const chevron = {
  "aria-hidden": true,
  viewBox: "0 0 16 16",
  className: "size-4",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const navButton = cn(
  "inline-flex size-8 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
  focusRing,
);

function sorted(a: IsoDate, b: IsoDate): DateRange {
  return compareDates(a, b) <= 0 ? { from: a, to: b } : { from: b, to: a };
}

function clamp(date: IsoDate, min?: IsoDate, max?: IsoDate): IsoDate {
  if (min && date < min) return min;
  if (max && date > max) return max;
  return date;
}

/**
 * Month grids on Monday-first weeks (the WAI-ARIA date grid). One tab stop:
 * arrows move a day or a week, Home/End go to the week's ends, Page Up/Down
 * a month (with Shift a year), Enter or Space choose. Every month is six rows,
 * so paging never changes the height.
 */
function Calendar({
  month,
  onMonthChange,
  numberOfMonths = 1,
  selected,
  anchor = null,
  onSelect,
  today,
  min,
  max,
  className,
}: CalendarProps) {
  const months = Array.from({ length: numberOfMonths }, (_, i) =>
    shiftMonth(month, i),
  );
  const lastMonth = months[months.length - 1] ?? month;
  const visible = (date: IsoDate) =>
    monthOf(date) >= month && monthOf(date) <= lastMonth;

  const initialFocus = () => {
    const candidates = [anchor, selected.to, selected.from, today];
    return (
      candidates.find((date): date is IsoDate => !!date && visible(date)) ??
      `${month}-01`
    );
  };
  const [focused, setFocused] = React.useState<IsoDate>(initialFocus);
  const [hovered, setHovered] = React.useState<IsoDate | null>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);
  const keyboardMove = React.useRef(false);

  // Paging with the arrows keeps the focused day; paging otherwise moves
  // it into view so the one tab stop is always on screen.
  const tabStop = visible(focused) ? focused : initialFocus();

  React.useEffect(() => {
    if (!keyboardMove.current) return;
    keyboardMove.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-date="${tabStop}"]`)
      ?.focus();
  }, [tabStop]);

  const preview: DateRange | null = anchor
    ? sorted(anchor, hovered ?? anchor)
    : selected.from && selected.to
      ? selected
      : null;

  const moveTo = (date: IsoDate) => {
    const next = clamp(date, min, max);
    keyboardMove.current = true;
    setFocused(next);
    if (anchor) setHovered(next);
    if (monthOf(next) < month) onMonthChange(monthOf(next));
    else if (monthOf(next) > lastMonth)
      onMonthChange(shiftMonth(monthOf(next), -(numberOfMonths - 1)));
  };

  const onKeyDown = (event: React.KeyboardEvent, date: IsoDate) => {
    const byMonth = (n: number) => {
      const target = shiftMonth(monthOf(date), n);
      const day = Number(date.slice(8));
      const grid = monthGrid(target).flat();
      const last = grid.filter((d) => monthOf(d) === target).pop()!;
      return `${target}-${String(Math.min(day, Number(last.slice(8)))).padStart(2, "0")}`;
    };
    const targets: Record<string, () => IsoDate> = {
      ArrowLeft: () => addDays(date, -1),
      ArrowRight: () => addDays(date, 1),
      ArrowUp: () => addDays(date, -7),
      ArrowDown: () => addDays(date, 7),
      Home: () => startOfWeek(date),
      End: () => addDays(startOfWeek(date), 6),
      PageUp: () => byMonth(event.shiftKey ? -12 : -1),
      PageDown: () => byMonth(event.shiftKey ? 12 : 1),
    };
    const target = targets[event.key];
    if (!target) return;
    event.preventDefault();
    moveTo(target());
  };

  return (
    <div
      ref={gridRef}
      data-slot="calendar"
      className={cn(
        "relative flex flex-col gap-4 [--cal-cell:2.75rem] sm:flex-row sm:gap-6 sm:[--cal-cell:2.25rem]",
        className,
      )}
      onMouseLeave={() => setHovered(null)}
    >
      <button
        type="button"
        aria-label="Previous month"
        className={cn(navButton, "absolute top-0 left-0")}
        onClick={() => onMonthChange(shiftMonth(month, -1))}
      >
        <svg {...chevron}>
          <path d="m10 3.5-4.5 4.5 4.5 4.5" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Next month"
        className={cn(navButton, "absolute top-0 right-0")}
        onClick={() => onMonthChange(shiftMonth(month, 1))}
      >
        <svg {...chevron}>
          <path d="m6 3.5 4.5 4.5L6 12.5" />
        </svg>
      </button>
      {months.map((shown) => {
        return (
          <MonthGrid
            key={shown}
            month={shown}
            today={today}
            tabStop={tabStop}
            selected={selected}
            preview={preview}
            anchor={anchor}
            min={min}
            max={max}
            onSelect={(date) => {
              setFocused(date);
              onSelect(date);
            }}
            onHover={(date) => anchor && setHovered(date)}
            onFocusDay={(date) => {
              setFocused(date);
              if (anchor) setHovered(date);
            }}
            onKeyDown={onKeyDown}
          />
        );
      })}
    </div>
  );
}

type MonthGridProps = {
  month: IsoMonth;
  today: IsoDate;
  tabStop: IsoDate;
  selected: DateRange;
  preview: DateRange | null;
  anchor: IsoDate | null;
  min?: IsoDate;
  max?: IsoDate;
  onSelect: (date: IsoDate) => void;
  onHover: (date: IsoDate) => void;
  onFocusDay: (date: IsoDate) => void;
  onKeyDown: (event: React.KeyboardEvent, date: IsoDate) => void;
};

function MonthGrid({
  month,
  today,
  tabStop,
  selected,
  preview,
  anchor,
  min,
  max,
  onSelect,
  onHover,
  onFocusDay,
  onKeyDown,
}: MonthGridProps) {
  const captionId = React.useId();
  const weeks = monthGrid(month);
  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto">
      <div
        id={captionId}
        aria-live="polite"
        className="flex h-8 items-center justify-center text-sm font-medium text-foreground"
      >
        {formatMonth(month)}
      </div>
      <table
        role="grid"
        aria-labelledby={captionId}
        className="w-full border-collapse sm:w-auto"
      >
        <thead aria-hidden="true">
          <tr>
            {WEEKDAY_SHORT.map((day) => (
              <th
                key={day}
                scope="col"
                className="h-8 w-[var(--cal-cell)] text-xs font-normal text-muted-foreground"
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((date) => {
                if (monthOf(date) !== month)
                  return (
                    <td
                      key={date}
                      role="presentation"
                      className="h-[var(--cal-cell)] p-0"
                    />
                  );
                const disabled = (!!min && date < min) || (!!max && date > max);
                const inRange =
                  !!preview && date >= preview.from && date <= preview.to;
                const isStart = !!preview && date === preview.from;
                const isEnd = !!preview && date === preview.to;
                const isSelected =
                  (!!selected.from && date === selected.from) ||
                  (!!selected.to && date === selected.to) ||
                  date === anchor;
                const weekday = week.indexOf(date);
                const band = inRange && !(isStart && isEnd);
                const opensLeft =
                  isStart || weekday === 0 || date.endsWith("-01");
                const closesRight =
                  isEnd || weekday === 6 || monthOf(addDays(date, 1)) !== month;
                return (
                  <td
                    key={date}
                    role="gridcell"
                    aria-selected={isSelected || (inRange && !anchor)}
                    className={cn(
                      "relative h-[var(--cal-cell)] p-0 text-center",
                      // The band sits on the cells so it joins across days; it
                      // starts and ends under the middle of the two end days and
                      // rounds off at each week's and month's edge.
                      band &&
                        "before:absolute before:inset-y-0.5 before:bg-accent",
                      band &&
                        (isStart
                          ? "before:left-1/2"
                          : opensLeft
                            ? "before:left-0.5 before:rounded-l-control"
                            : "before:left-0"),
                      band &&
                        (isEnd
                          ? "before:right-1/2"
                          : closesRight
                            ? "before:right-0.5 before:rounded-r-control"
                            : "before:right-0"),
                    )}
                  >
                    <button
                      type="button"
                      data-date={date}
                      data-range-start={isStart || undefined}
                      data-range-end={isEnd || undefined}
                      tabIndex={date === tabStop ? 0 : -1}
                      disabled={disabled}
                      aria-label={formatDateLong(date)}
                      aria-current={date === today ? "date" : undefined}
                      onClick={() => onSelect(date)}
                      onMouseEnter={() => onHover(date)}
                      onFocus={() => onFocusDay(date)}
                      onKeyDown={(event) => onKeyDown(event, date)}
                      className={cn(
                        "relative z-[1] inline-flex size-[var(--cal-cell)] items-center justify-center rounded-control text-sm tabular-nums text-foreground",
                        focusRing,
                        "hover:bg-muted disabled:pointer-events-none disabled:opacity-40",
                        inRange && !(isStart || isEnd) && "hover:bg-card/60",
                        (isStart || isEnd) &&
                          "bg-primary font-semibold text-primary-foreground shadow-button hover:bg-primary-hover",
                        date === today &&
                          "after:absolute after:bottom-1 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-current",
                      )}
                    >
                      {Number(date.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { Calendar };
export type { CalendarProps };
